import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  first,
  map,
  Observable,
  of,
  ReplaySubject,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import { PaginationState } from 'src/app/components/shared/pagination/pagination.interface';
import { SearchState } from 'src/app/interfaces/search.interface';
import { EmptyResponse, PatientDistributionFilter, RelationGeography } from 'src/app/swagger-api';
import { SortCategories } from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import { UsersSearchService } from '../../search/users-search.service';
import { paginationParams } from '../../shared/list-controller/list-controller';
import {
  ConnectionRequest,
  RelationRequests,
  RelationRequestsCount,
  SuggestedUser,
  SuggestedUsers,
  UserBasic,
} from '../relation-request.interface';

@Injectable()
export abstract class UserRelationRequestService<T extends ConnectionRequest> implements OnDestroy {
  private readonly INITIAL_RELATION_REQUESTS: RelationRequests<T> = {
    items: [],
    totalCount: 0,
  };
  private readonly INITIAL_TOTAL_REQUESTS: RelationRequestsCount = {
    verifiedCount: 0,
    requestsSent: 0,
    requestsReceived: 0,
  };

  private inProgress$: ReplaySubject<boolean> = new ReplaySubject();
  private userListChanges$: BehaviorSubject<UserBasic[]> = new BehaviorSubject<UserBasic[]>([]);
  private totalRequestsChanges$: BehaviorSubject<RelationRequestsCount> = new BehaviorSubject(
    this.INITIAL_TOTAL_REQUESTS,
  );

  private relationRequestsChanges$: BehaviorSubject<RelationRequests<T>> = new BehaviorSubject(
    this.INITIAL_RELATION_REQUESTS,
  );
  private relationGeographyFetched$: Subject<RelationGeography> = new Subject();

  private relationStatusChangesTrigger$: Subject<void> = new Subject();
  private loadDataTrigger$: Subject<Observable<RelationRequests<T>>> = new Subject();

  private subscriptions$: Subscription[] = [];

  private usersSearchService = inject(UsersSearchService);

  public constructor() {
    this.subscribeOnloadData();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
    subjectsComplete([
      this.inProgress$,
      this.loadDataTrigger$,
      this.userListChanges$,
      this.totalRequestsChanges$,
      this.relationStatusChangesTrigger$,
      this.relationRequestsChanges$,
      this.relationGeographyFetched$,
    ]);
  }

  public get relationStatusChanges$(): Observable<void> {
    return this.relationStatusChangesTrigger$.asObservable();
  }

  public get relatedUsersList$(): Observable<UserBasic[]> {
    return this.userListChanges$.asObservable();
  }

  public get totalRequests$(): Observable<RelationRequestsCount> {
    return this.totalRequestsChanges$.asObservable();
  }

  public get relationRequestsList$(): Observable<RelationRequests<T>> {
    return this.relationRequestsChanges$.asObservable();
  }

  public get relationGeographyFetched(): Observable<RelationGeography> {
    return this.relationGeographyFetched$.asObservable();
  }

  public get suggestionsUserList$(): Observable<SuggestedUsers> {
    return this.usersSearchService.suggestionsList$;
  }

  public get isLoading$(): Observable<boolean> {
    return combineLatest([this.inProgress$.pipe(startWith(false)), this.usersSearchService.isLoading$]).pipe(
      map(([inProgres, inSearch]: [boolean, boolean]) => inProgres || inSearch),
    );
  }

  public loadSuggestionUsers(state: SearchState): void {
    this.usersSearchService.suggestUsers(this.updateSearchState(state));
  }

  public loadAcceptedRequest(pagination: PaginationState, sorting?: SortCategories, filtering?: string): void {
    const [offset, limit] = paginationParams(pagination);
    this.loadDataTrigger$.next(this.fetchAcceptedRequests(offset, limit, sorting, filtering));
  }

  public loadReceivedRequest(pagination: PaginationState, sorting?: SortCategories, filtering?: string): void {
    const [offset, limit] = paginationParams(pagination);
    this.loadDataTrigger$.next(this.fetchReceivedRequests(offset, limit, sorting, filtering));
  }

  public loadSentRequest(pagination: PaginationState, sorting?: SortCategories, filtering?: string): void {
    const [offset, limit] = paginationParams(pagination);
    this.loadDataTrigger$.next(this.fetchSentRequests(offset, limit, sorting, filtering));
  }

  // use for admin
  public loadRegularUserRelation(regularUserId: string, pagination: PaginationState): void {
    const [offset, limit] = paginationParams(pagination);
    this.loadDataTrigger$.next(this.fetchRegularUserRelation(regularUserId, offset, limit));
  }

  public loadRelationGeography(userId: string, filterBy?: PatientDistributionFilter, filterValue?: string): void {
    this.fetchRelationGeography(userId, filterBy, filterValue)
      .pipe(catchError(() => of([])))
      .subscribe((result: RelationGeography) => {
        this.relationGeographyFetched$.next(result);
      });
  }

  public getRelationGeography(
    userId: string,
    filterBy?: PatientDistributionFilter,
    filterValue?: string,
  ): Observable<RelationGeography> {
    return this.fetchRelationGeography(userId, filterBy, filterValue);
  }

  public loadRequestsTotalCount(): void {
    this.subscriptions$.push(
      this.fetchTotalCountRequests()
        .pipe(
          first(),
          catchError(() => of(this.INITIAL_TOTAL_REQUESTS)),
          tap((result: RelationRequestsCount) => this.totalRequestsChanges$.next(result)),
        )
        .subscribe(),
    );
  }

  public acceptRelationRequest(relationRequest: T): void {
    this.performAction(this.accept(relationRequest));
  }

  public rejectRelationRequest(relationRequest: T): void {
    this.performAction(this.reject(relationRequest));
  }

  public removeRelationRequest(relationRequest: T): void {
    this.performAction(this.remove(relationRequest));
  }

  public createRelationRequest(relatedUser: SuggestedUser): void {
    this.performAction(this.create(relatedUser));
  }

  public removeRelationByUser(relatedUser: SuggestedUser): void {
    this.performAction(this.removeByUser(relatedUser));
  }

  public acceptRelationByUser(relatedUser: SuggestedUser): void {
    this.performAction(this.acceptByUser(relatedUser));
  }

  public resetDataState(): void {
    this.userListChanges$.next([]);
    this.totalRequestsChanges$.next(this.INITIAL_TOTAL_REQUESTS);
    this.relationRequestsChanges$.next(this.INITIAL_RELATION_REQUESTS);
  }

  protected abstract updateSearchState(state: SearchState): SearchState;

  protected abstract fetchTotalCountRequests(): Observable<RelationRequestsCount>;

  protected abstract fetchAcceptedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
    filtering?: string,
  ): Observable<RelationRequests<T>>;

  protected abstract fetchReceivedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
    filtering?: string,
  ): Observable<RelationRequests<T>>;

  protected abstract fetchSentRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
    filtering?: string,
  ): Observable<RelationRequests<T>>;

  protected abstract fetchRegularUserRelation(
    userId: string,
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<T>>;

  protected abstract fetchRelationGeography(
    userId: string,
    filterBy?: PatientDistributionFilter,
    filterValue?: string,
  ): Observable<RelationGeography>;

  protected abstract accept(relationRequest: T): Observable<EmptyResponse>;

  protected abstract reject(relationRequest: T): Observable<EmptyResponse>;

  protected abstract remove(relationRequest: T): Observable<EmptyResponse>;

  protected abstract create(user: SuggestedUser): Observable<EmptyResponse>;

  protected abstract removeByUser(user: SuggestedUser): Observable<EmptyResponse>;

  protected abstract acceptByUser(user: SuggestedUser): Observable<EmptyResponse>;

  private performAction<K>(apiCall$: Observable<K>): void {
    this.subscriptions$.push(
      apiCall$
        .pipe(
          first(),
          catchError(({ status }: HttpErrorResponse) => (status !== HttpStatusCode.BadRequest ? EMPTY : of({}))),
          tap(() => {
            this.relationStatusChangesTrigger$.next();
          }),
        )
        .subscribe(),
    );
  }

  private subscribeOnloadData(): void {
    this.subscriptions$.push(
      this.loadDataTrigger$
        .pipe(
          tap(() => this.inProgress$.next(true)),
          switchMap((apiCall$: Observable<RelationRequests<T>>) =>
            apiCall$.pipe(
              catchError(() => of(this.INITIAL_RELATION_REQUESTS)),
              tap((result: RelationRequests<T>) => {
                this.inProgress$.next(false);
                this.relationRequestsChanges$.next(result);
                this.userListChanges$.next(result.items.map((item: T) => item.relatedUser));
              }),
            ),
          ),
        )
        .subscribe(),
    );
  }
}
